/**
 * Модели предметной области — порт `core/domain` Android-версии.
 *
 * Значения перечислений совпадают со строками, которые лежат в базе на Android
 * (`CaseStatus.Active`, `DocumentType.Lawsuit`): архив, снятый на телефоне, должен
 * читаться здесь без перевода. Поэтому это не «свои» названия, а те же самые.
 *
 * Время — миллисекунды эпохи, как в Room. Даты без времени — строка `ГГГГ-ММ-ДД`.
 */

export type CaseType = 'Consultation' | 'ClaimWork' | 'CourtCase' | 'Enforcement' | 'Other';

/**
 * Состояние дела. Архива здесь нет намеренно — [D-38](docs/DECISIONS.md): он отличался
 * от «Завершено» ровно одним и партнёр-юрист разницы не увидела.
 */
export type CaseStatus = 'Active' | 'Suspended' | 'Completed';

/** Дела, в которые кладут новое. Одно место на правило, а не четыре копии. */
export const OPEN_CASE_STATUSES: readonly CaseStatus[] = ['Active', 'Suspended'];

/** Внешняя ссылка дела: карточка на сайте суда, папка в облаке. */
export interface CaseLink {
  title: string;
  url: string;
}

export interface Case {
  id: string;
  /** Единственное обязательное поле: создание дела не должно быть анкетой. */
  title: string;
  clientId: string | null;
  type: CaseType;
  status: CaseStatus;
  /** Своё название типа — только у `Other`. */
  typeOther: string | null;
  description: string | null;
  notes: string | null;
  links: CaseLink[];
  /** Закреплённое дело стоит выше любой сортировки — D-13. */
  isPinned: boolean;
  /** Место в своём порядке. Пишется в обход `updatedAt`. */
  sortOrder: number;
  createdAt: number;
  updatedAt: number;
}

export interface Client {
  id: string;
  fullName: string;
  phone: string | null;
  email: string | null;
  /** `ГГГГ-ММ-ДД`. */
  birthDate: string | null;
  address: string | null;
  notes: string | null;
  createdAt: number;
  updatedAt: number;
}

export type DocumentType =
  | 'Lawsuit'
  | 'Motion'
  | 'Objection'
  | 'CourtAct'
  | 'Claim'
  | 'Contract'
  | 'PowerOfAttorney'
  | 'Expertise'
  | 'WritOfExecution'
  | 'Extract'
  | 'Correspondence'
  | 'Payment'
  | 'CaseMaterials'
  | 'Other';

/**
 * Откуда файл попал в дело. Различие нужно объединению документов: у скана и снимков
 * текстового слоя нет изначально, терять нечего; у принесённого PDF — есть.
 */
export type DocumentSource = 'Scanned' | 'Photo' | 'Imported';

export interface CaseDocument {
  id: string;
  title: string;
  caseId: string | null;
  clientId: string | null;
  /** Имя файла в хранилище; путь собирается из `caseId` — ARCHITECTURE.md. */
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  pageCount: number | null;
  isFavorite: boolean;
  type: DocumentType;
  source: DocumentSource;
  sortOrder: number;
  addedAt: number;
  createdAt: number;
  updatedAt: number;
}

/**
 * Событие: задача, заседание или встреча. Объединены намеренно — «Задачи» и «Календарь»
 * это разные представления одной сущности.
 */
export type EventType = 'Task' | 'Hearing' | 'Meeting' | 'Other';

export interface CaseEvent {
  id: string;
  title: string;
  type: EventType;
  startAt: number;
  endAt: number | null;
  description: string | null;
  caseId: string | null;
  clientId: string | null;
  reminderMinutesBefore: number | null;
  isCompleted: boolean;
  deadlineAt: number | null;
  createdAt: number;
  updatedAt: number;
}

/**
 * Момент срабатывания напоминания или `null`, если напоминать не о чем.
 *
 * Прошедшее напоминание не ставится заново: иначе после перезапуска юрист получил бы
 * пачку уведомлений о том, что и так прошло.
 */
export function reminderAt(event: CaseEvent, now: number): number | null {
  if (event.isCompleted || event.reminderMinutesBefore === null) return null;

  const moment = (event.deadlineAt ?? event.startAt) - event.reminderMinutesBefore * 60_000;
  return moment > now ? moment : null;
}

/** Пользовательский тег дела: «банкротство», «срочное», «VIP». */
export interface Tag {
  id: string;
  name: string;
}
